import React, { useState, useEffect, useMemo } from 'react';
import { collection, getDocs, query, orderBy, where, Timestamp, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../services/firebase';
import { LoginEvent, QuestionBankAPILog, UsageFilter, DeviceInfo, UserLocation } from '../types/usage';
import { FirebaseUser } from '../types/user';
import RecordDetailsModal from '../components/RecordDetailsModal';
import './stylesheets/UserManagement.css';
import './stylesheets/Usage.css';

const Usage: React.FC = () => {
  const [loginEvents, setLoginEvents] = useState<(LoginEvent & { id: string })[]>([]);
  const [apiLogs, setApiLogs] = useState<(QuestionBankAPILog & { id: string })[]>([]);
  const [users, setUsers] = useState<FirebaseUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState<'logins' | 'api'>('logins');
  const [search, setSearch] = useState('');
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' }>({ key: 'timestamp', direction: 'desc' });
  const [filter, setFilter] = useState<UsageFilter>({
    eventType: 'all',
    dateFrom: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000), // Last 7 days
    dateTo: new Date()
  });
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<(LoginEvent & { id: string }) | (QuestionBankAPILog & { id: string }) | null>(null);
  const [modalType, setModalType] = useState<'login' | 'api'>('login');
  const [hideTestUsers, setHideTestUsers] = useState(true);

  const handleRecordClick = (record: (LoginEvent & { id: string }) | (QuestionBankAPILog & { id: string }), type: 'login' | 'api') => {
    setSelectedRecord(record);
    setModalType(type);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedRecord(null);
  };

  const handleDeleteAllDisplayed = async () => {
    const currentData = activeView === 'logins' ? sortedLoginEvents : sortedAPILogs;
    const collectionName = activeView === 'logins' ? 'loginEvents' : 'questionBankAPILogs';
    
    if (currentData.length === 0) {
      alert('No records to delete.');
      return;
    }

    const confirmMessage = `Are you sure you want to delete all ${currentData.length} ${activeView === 'logins' ? 'login events' : 'API logs'} currently displayed? This action cannot be undone.`;
    
    if (window.confirm(confirmMessage)) {
      setLoading(true);
      try {
        const deletePromises = currentData.map(record => 
          deleteDoc(doc(db, collectionName, record.id))
        );
        
        await Promise.all(deletePromises);
        
        // Reload data to refresh the UI
        await loadData();
        
        alert(`Successfully deleted ${currentData.length} ${activeView === 'logins' ? 'login events' : 'API logs'}.`);
      } catch (error) {
        console.error('Error deleting records:', error);
        alert('Error deleting records. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleDeleteSingleRecord = async (record: (LoginEvent & { id: string }) | (QuestionBankAPILog & { id: string }), type: 'login' | 'api') => {
    const collectionName = type === 'login' ? 'loginEvents' : 'questionBankAPILogs';
    const recordType = type === 'login' ? 'login event' : 'API log';
    
    const confirmMessage = `Are you sure you want to delete this ${recordType}? This action cannot be undone.`;
    
    if (window.confirm(confirmMessage)) {
      try {
        await deleteDoc(doc(db, collectionName, record.id));
        
        // Close the modal
        closeModal();
        
        // Reload data to refresh the UI
        await loadData();
        
        alert(`Successfully deleted the ${recordType}.`);
      } catch (error) {
        console.error('Error deleting record:', error);
        alert('Error deleting record. Please try again.');
      }
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([loadLoginEvents(), loadAPILogs(), loadUsers()]);
    } catch (error) {
      console.error('Error loading usage data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadLoginEvents = async () => {
    try {
      const loginEventsRef = collection(db, 'loginEvents');
      const q = query(loginEventsRef, orderBy('timestamp', 'desc'));
      const snapshot = await getDocs(q);
      const events = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as (LoginEvent & { id: string })[];
      setLoginEvents(events);
    } catch (error) {
      console.error('Error loading login events:', error);
    }
  };

  const loadAPILogs = async () => {
    try {
      const apiLogsRef = collection(db, 'questionBankAPILogs');
      const q = query(apiLogsRef, orderBy('timestamp', 'desc'));
      const snapshot = await getDocs(q);
      const logs = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as (QuestionBankAPILog & { id: string })[];
      setApiLogs(logs);
    } catch (error) {
      console.error('Error loading API logs:', error);
    }
  };

  const loadUsers = async () => {
    try {
      const usersCollection = collection(db, 'users');
      const usersSnapshot = await getDocs(usersCollection);
      const usersList = usersSnapshot.docs.map(doc => ({
        uid: doc.id,
        ...doc.data()
      })) as FirebaseUser[];
      setUsers(usersList);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const getUserEmail = (userId: string): string => {
    const user = users.find(u => u.uid === userId);
    return user?.email || userId;
  };

  const formatTimestamp = (timestamp: any): string => {
    if (timestamp instanceof Timestamp) {
      return timestamp.toDate().toLocaleString();
    }
    if (typeof timestamp === 'string') {
      return new Date(timestamp).toLocaleString();
    }
    return 'Unknown';
  };

  const formatDeviceInfo = (deviceInfo: DeviceInfo): string => {
    return `${deviceInfo.platform} / ${deviceInfo.browser} ${deviceInfo.isMobile ? '(Mobile)' : '(Desktop)'}`;
  };

  const formatLocation = (location: UserLocation): string => {
    return `${location.country} (${location.timezone})`;
  };

  const filteredLoginEvents = useMemo(() => {
    let filtered = loginEvents.filter(event => {
      if (selectedUserId && event.userId !== selectedUserId) return false;
      if (filter.dateFrom || filter.dateTo) {
        const eventDate = event.timestamp instanceof Timestamp ? 
          event.timestamp.toDate() : new Date(event.timestamp);
        if (filter.dateFrom && eventDate < filter.dateFrom) return false;
        if (filter.dateTo) {
          // Make the To date inclusive by setting it to end of day
          const endOfDay = new Date(filter.dateTo);
          endOfDay.setHours(23, 59, 59, 999);
          if (eventDate > endOfDay) return false;
        }
      }
      if (filter.country && event.location.country !== filter.country) return false;
      return true;
    });

    // Filter out test users if hideTestUsers is enabled
    if (hideTestUsers) {
      filtered = filtered.filter(event => {
        const userEmail = getUserEmail(event.userId);
        return userEmail !== 'admin@mockmedic.com' && 
               userEmail !== 'testuser@mockmedic.com' &&
               !userEmail.includes('test');
      });
    }

    // Apply search filter
    const term = search.trim().toLowerCase();
    if (term) {
      filtered = filtered.filter(event => {
        const userEmail = getUserEmail(event.userId);
        const deviceInfo = formatDeviceInfo(event.deviceInfo);
        const location = formatLocation(event.location);
        return [userEmail, event.loginMethod, deviceInfo, location, event.userId]
          .filter(Boolean)
          .some(val => String(val).toLowerCase().includes(term));
      });
    }

    return filtered;
  }, [loginEvents, selectedUserId, filter, search, users, hideTestUsers]);

  const filteredAPILogs = useMemo(() => {
    let filtered = apiLogs.filter(log => {
      if (selectedUserId && log.userId !== selectedUserId) return false;
      if (filter.dateFrom || filter.dateTo) {
        const logDate = new Date(log.timestamp);
        if (filter.dateFrom && logDate < filter.dateFrom) return false;
        if (filter.dateTo) {
          // Make the To date inclusive by setting it to end of day
          const endOfDay = new Date(filter.dateTo);
          endOfDay.setHours(23, 59, 59, 999);
          if (logDate > endOfDay) return false;
        }
      }
      if (filter.specialty && log.specialty !== filter.specialty) return false;
      if (filter.country && log.userLocation.country !== filter.country) return false;
      return true;
    });

    // Filter out test users if hideTestUsers is enabled
    if (hideTestUsers) {
      filtered = filtered.filter(log => {
        return log.userEmail !== 'admin@mockmedic.com' && 
               log.userEmail !== 'testuser@mockmedic.com' &&
               !log.userEmail.includes('test');
      });
    }

    // Apply search filter
    const term = search.trim().toLowerCase();
    if (term) {
      filtered = filtered.filter(log => {
        const deviceInfo = formatDeviceInfo(log.deviceInfo);
        const location = formatLocation(log.userLocation);
        const specialty = log.specialty.replace('-', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());
        return [log.userEmail, log.requestType, specialty, deviceInfo, location, log.userId]
          .filter(Boolean)
          .some(val => String(val).toLowerCase().includes(term));
      });
    }

    return filtered;
  }, [apiLogs, selectedUserId, filter, search, hideTestUsers]);

  const uniqueSpecialties = useMemo(() => {
    return Array.from(new Set(apiLogs.map(log => log.specialty))).sort();
  }, [apiLogs]);

  const uniqueCountries = useMemo(() => {
    const loginCountries = loginEvents.map(event => event.location.country);
    const apiCountries = apiLogs.map(log => log.userLocation.country);
    return Array.from(new Set([...loginCountries, ...apiCountries])).sort();
  }, [loginEvents, apiLogs]);

  const availableUsers = useMemo(() => {
    if (activeView === 'logins') {
      const userIds = Array.from(new Set(filteredLoginEvents.map(event => event.userId)));
      return users.filter(user => userIds.includes(user.uid)).sort((a, b) => a.email.localeCompare(b.email));
    } else {
      const userEmails = Array.from(new Set(filteredAPILogs.map(log => log.userEmail)));
      return users.filter(user => userEmails.includes(user.email)).sort((a, b) => a.email.localeCompare(b.email));
    }
  }, [activeView, filteredLoginEvents, filteredAPILogs, users]);

  const getOverviewStats = () => {
    const totalLogins = filteredLoginEvents.length;
    const totalAPIRequests = filteredAPILogs.length;
    const uniqueUsers = new Set([
      ...filteredLoginEvents.map(e => e.userId),
      ...filteredAPILogs.map(l => l.userId)
    ]).size;
    const avgResponseTime = filteredAPILogs.length > 0 ? 
      Math.round(filteredAPILogs.reduce((sum, log) => sum + log.response.responseTimeMs, 0) / filteredAPILogs.length) : 0;

    return { totalLogins, totalAPIRequests, uniqueUsers, avgResponseTime };
  };

  const toggleSort = (key: string) => {
    setSortConfig(prev => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: key === 'timestamp' ? 'desc' : 'asc' };
    });
  };

  const sortIndicator = (key: string) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
  };

  const sortedLoginEvents = useMemo(() => {
    return [...filteredLoginEvents].sort((a, b) => {
      const { key, direction } = sortConfig;
      const dir = direction === 'asc' ? 1 : -1;
      const aVal = (a as any)[key];
      const bVal = (b as any)[key];

      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;

      if (key === 'timestamp') {
        const aTime = aVal instanceof Timestamp ? aVal.toDate().getTime() : new Date(aVal).getTime();
        const bTime = bVal instanceof Timestamp ? bVal.toDate().getTime() : new Date(bVal).getTime();
        return (aTime - bTime) * dir;
      }

      return String(aVal).localeCompare(String(bVal)) * dir;
    });
  }, [filteredLoginEvents, sortConfig]);

  const sortedAPILogs = useMemo(() => {
    return [...filteredAPILogs].sort((a, b) => {
      const { key, direction } = sortConfig;
      const dir = direction === 'asc' ? 1 : -1;
      const aVal = (a as any)[key];
      const bVal = (b as any)[key];

      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;

      if (key === 'timestamp') {
        const aTime = new Date(aVal).getTime();
        const bTime = new Date(bVal).getTime();
        return (aTime - bTime) * dir;
      }

      if (key === 'response.responseTimeMs') {
        return ((a.response?.responseTimeMs || 0) - (b.response?.responseTimeMs || 0)) * dir;
      }

      return String(aVal).localeCompare(String(bVal)) * dir;
    });
  }, [filteredAPILogs, sortConfig]);

  const stats = getOverviewStats();

  if (loading) {
    return <div className="loading">Loading usage analysis...</div>;
  }

  const currentData = activeView === 'logins' ? sortedLoginEvents : sortedAPILogs;

  return (
    <div className="operation-content">
      <h2>Usage Analysis</h2>

      {/* Analytics Cards */}
      <div className="analytics-grid" style={{marginBottom: '30px'}}>
        <div className="analytics-card">
          <h3>Total Logins</h3>
          <div className="analytics-number">{stats.totalLogins}</div>
        </div>
        <div className="analytics-card">
          <h3>API Requests</h3>
          <div className="analytics-number">{stats.totalAPIRequests}</div>
        </div>
        <div className="analytics-card">
          <h3>Unique Users</h3>
          <div className="analytics-number">{stats.uniqueUsers}</div>
        </div>
        <div className="analytics-card">
          <h3>Avg Response Time</h3>
          <div className="analytics-number">{stats.avgResponseTime}ms</div>
        </div>
      </div>

      {/* View Toggle Buttons */}
      <div style={{
        display: 'flex', 
        justifyContent: 'space-between', 
        marginBottom: '20px',
        gap: '12px',
        flexWrap: 'wrap',
        alignItems: 'center'
      }}>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <button
            onClick={() => setActiveView('logins')}
            style={{
              background: activeView === 'logins' ? '#64b5f6' : '#6c757d',
              color: 'white',
              border: 'none',
              padding: '12px 20px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              transition: 'all 0.3s ease'
            }}
          >
            Login Events ({filteredLoginEvents.length})
          </button>
          <button
            onClick={() => setActiveView('api')}
            style={{
              background: activeView === 'api' ? '#64b5f6' : '#6c757d',
              color: 'white',
              border: 'none',
              padding: '12px 20px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              transition: 'all 0.3s ease'
            }}
          >
            API Logs ({filteredAPILogs.length})
          </button>
        </div>
        
        <button
          onClick={() => setHideTestUsers(!hideTestUsers)}
          style={{
            background: hideTestUsers ? '#ff9800' : '#2196f3',
            color: 'white',
            border: 'none',
            padding: '12px 20px',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '600',
            transition: 'all 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = hideTestUsers ? '#f57c00' : '#1976d2';
            e.currentTarget.style.transform = 'translateY(-1px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = hideTestUsers ? '#ff9800' : '#2196f3';
            e.currentTarget.style.transform = 'translateY(0)';
          }}
        >
          <span>{hideTestUsers ? '👁️' : '🚫'}</span>
          {hideTestUsers ? 'Show Test Users' : 'Hide Test Users'}
        </button>
      </div>

      <div className="usage-filters-wrapper">
        <div className="filter-group">
          <label>
            User:
            <select 
              value={selectedUserId} 
              onChange={(e) => setSelectedUserId(e.target.value)}
            >
              <option value="">All Users</option>
              {availableUsers.map(user => (
                <option key={user.uid} value={user.uid}>
                  {user.email}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="filter-group">
          <label>
            From:
            <input 
              type="date" 
              value={filter.dateFrom ? filter.dateFrom.toISOString().split('T')[0] : ''} 
              onChange={(e) => setFilter(prev => ({ 
                ...prev, 
                dateFrom: e.target.value ? new Date(e.target.value) : undefined 
              }))}
            />
          </label>
        </div>

        <div className="filter-group">
          <label>
            To:
            <input 
              type="date" 
              value={filter.dateTo ? filter.dateTo.toISOString().split('T')[0] : ''} 
              onChange={(e) => setFilter(prev => ({ 
                ...prev, 
                dateTo: e.target.value ? new Date(e.target.value) : undefined 
              }))}
            />
          </label>
        </div>

        {activeView === 'api' && (
          <div className="filter-group">
            <label>
              Specialty:
              <select 
                value={filter.specialty || ''} 
                onChange={(e) => setFilter(prev => ({ 
                  ...prev, 
                  specialty: e.target.value || undefined 
                }))}
              >
                <option value="">All Specialties</option>
                {uniqueSpecialties.map(specialty => (
                  <option key={specialty} value={specialty}>
                    {specialty.replace('-', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}
                  </option>
                ))}
              </select>
            </label>
          </div>
        )}

        <div className="filter-group">
          <label>
            Country:
            <select 
              value={filter.country || ''} 
              onChange={(e) => setFilter(prev => ({ 
                ...prev, 
                country: e.target.value || undefined 
              }))}
            >
              <option value="">All Countries</option>
              {uniqueCountries.map(country => (
                <option key={country} value={country}>
                  {country}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      <div className="users-table-wrapper">
        <div style={{marginBottom: '16px', display:'flex', flexWrap:'wrap', gap:'12px', alignItems:'center'}}>
          <input
            type="text"
            placeholder="Search by user, device, location..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              padding: '10px 14px',
              borderRadius: 6,
              border: '5px solid #949494ff',
              background: '#585757ff',
              color: '#f3f3f3ff',
              flex: '1 1 260px'
            }}
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              style={{
                background:'#444',
                color:'#eee',
                border:'1px solid #555',
                padding:'10px 16px',
                borderRadius:6,
                cursor:'pointer'
              }}
            >Clear</button>
          )}
          
          <button
            onClick={handleDeleteAllDisplayed}
            disabled={currentData.length === 0 || loading}
            style={{
              background: currentData.length === 0 || loading ? '#666' : '#dc3545',
              color: 'white',
              border: 'none',
              padding: '10px 16px',
              borderRadius: 6,
              cursor: currentData.length === 0 || loading ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
            onMouseEnter={(e) => {
              if (currentData.length > 0 && !loading) {
                e.currentTarget.style.background = '#c82333';
              }
            }}
            onMouseLeave={(e) => {
              if (currentData.length > 0 && !loading) {
                e.currentTarget.style.background = '#dc3545';
              }
            }}
            title={currentData.length === 0 ? 'No records to delete' : `Delete all ${currentData.length} displayed records`}
          >
            🗑️ Delete All ({currentData.length})
          </button>
          
          <span style={{marginLeft:'auto', color:'#64b5f6', fontWeight:600, fontSize:'16px', paddingRight:'18px'}}>
            {currentData.length} result{currentData.length === 1 ? '' : 's'}
          </span>
        </div>
        
        <div className="users-table-container">
          <table className="users-table">{activeView === 'logins' ? (
            <>
              <thead>
                <tr>
                  <th style={{cursor:'pointer'}} onClick={() => toggleSort('timestamp')}>
                    Timestamp{sortIndicator('timestamp')}
                  </th>
                  <th style={{cursor:'pointer'}} onClick={() => toggleSort('userId')}>
                    User{sortIndicator('userId')}
                  </th>
                  <th>Device</th>
                  <th>Location</th>
                </tr>
              </thead>
              <tbody>
                {sortedLoginEvents.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="no-data">
                      No login events found for the selected criteria.
                    </td>
                  </tr>
                ) : (
                  sortedLoginEvents.map(event => (
                    <tr key={event.id} 
                        className="clickable-row"
                        onClick={() => handleRecordClick(event, 'login')}
                        title="Click to view details">
                      <td data-label="Timestamp">{formatTimestamp(event.timestamp)}</td>
                      <td data-label="User">{getUserEmail(event.userId)}</td>
                      <td data-label="Device">{formatDeviceInfo(event.deviceInfo)}</td>
                      <td data-label="Location">{formatLocation(event.location)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </>
          ) : (
            <>
              <thead>
                <tr>
                  <th style={{cursor:'pointer'}} onClick={() => toggleSort('timestamp')}>
                    Timestamp{sortIndicator('timestamp')}
                  </th>
                  <th style={{cursor:'pointer'}} onClick={() => toggleSort('userEmail')}>
                    User{sortIndicator('userEmail')}
                  </th>
                  <th style={{cursor:'pointer'}} onClick={() => toggleSort('requestType')}>
                    Request Type{sortIndicator('requestType')}
                  </th>
                  <th style={{cursor:'pointer'}} onClick={() => toggleSort('specialty')}>
                    Specialty{sortIndicator('specialty')}
                  </th>
                  <th style={{cursor:'pointer'}} onClick={() => toggleSort('status')}>
                    Status{sortIndicator('status')}
                  </th>
                  <th style={{cursor:'pointer'}} onClick={() => toggleSort('response.responseTimeMs')}>
                    Response Time{sortIndicator('response.responseTimeMs')}
                  </th>
                  <th>Device</th>
                  <th>Location</th>
                </tr>
              </thead>
              <tbody>
                {sortedAPILogs.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="no-data">
                      No API logs found for the selected criteria.
                    </td>
                  </tr>
                ) : (
                  sortedAPILogs.map(log => (
                    <tr key={log.id} 
                        className={`clickable-row ${log.status === 200 ? 'success' : 'error'} ${!log.response.hasData ? 'no-data-flag' : ''}`}
                        onClick={() => handleRecordClick(log, 'api')}
                        title="Click to view details"
                        style={{
                          backgroundColor: !log.response.hasData ? '#4a1a1a' : undefined,
                          borderLeft: !log.response.hasData ? '4px solid #ff4444' : undefined,
                          color: !log.response.hasData ? '#ffcccc' : undefined
                        }}>
                      <td data-label="Timestamp">{formatTimestamp(log.timestamp)}</td>
                      <td data-label="User">{log.userEmail}</td>
                      <td data-label="Request Type">{log.requestType}</td>
                      <td data-label="Specialty">{log.specialty.replace('-', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}</td>
                      <td data-label="Status">
                        <span className={`status-badge ${log.status === 200 ? 'success' : 'error'}`}>
                          {log.status}
                        </span>
                      </td>
                      <td data-label="Response Time">{log.response.responseTimeMs}ms</td>
                      <td data-label="Device">{formatDeviceInfo(log.deviceInfo)}</td>
                      <td data-label="Location">{formatLocation(log.userLocation)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </>
          )}
          </table>
        </div>
      </div>
      
      <RecordDetailsModal
        isOpen={isModalOpen}
        onClose={closeModal}
        record={selectedRecord}
        type={modalType}
        getUserEmail={getUserEmail}
        onDelete={handleDeleteSingleRecord}
      />
    </div>
  );
};

export default Usage;